// 性能計測用のユーティリティ。既存の挙動は一切変えず、集計だけを溜める。
//
// 手動操作:
//   window.__perf.dump()    要約を今すぐ出す
//   window.__perf.reset()   集計をクリア
//   window.__perf.raw()     生データを取得
//   window.__perf.watch()   メインスレッド停止の監視を開始(読み込み時に自動開始済み)

// 計測の有効/無効。true にすると全計測([perf]ログ・watchdog・window.__perf)が復活する。
const ENABLED = false;

const state = {
    spans: [],
    master: new Map(),   // key -> { count, notFound, totalMs, maxMs }
    requests: 0,
    inflight: 0,
    maxInflight: 0,
    firstRequestAt: 0,
    lastResponseAt: 0,
    render: new Map(),   // コンポーネント名 -> 回数
    action: new Map(),   // Redux action type -> 回数
    stalls: []           // メインスレッドが止まった記録
};

let dumpTimer = null;

function now() {
    return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
}

// ---- 区間計測 ----
export function perfSpanStart(name) {
    if (!ENABLED) return null;
    return { name: name, t0: now() };
}

export function perfSpanEnd(token, extra) {
    if (!ENABLED || token === null || token === undefined) return 0;
    const ms = now() - token.t0;
    state.spans.push({ name: token.name, ms: ms, extra: extra || '' });
    console.log('[perf] ' + token.name + ' = ' + ms.toFixed(1) + 'ms' + (extra ? ' ' + extra : ''));
    return ms;
}

export function perfSpanEndAfterPaint(token, extra) {
    if (!ENABLED || token === null || token === undefined) return;
    const raf = (typeof requestAnimationFrame === 'function') ? requestAnimationFrame : ((cb) => setTimeout(cb, 16));
    raf(() => setTimeout(() => perfSpanEnd(token, extra), 0));
}

// ---- マスタ照会 ----
export function perfMasterRequest(dataid, code, keyinfo) {
    if (!ENABLED) return null;
    const key = String(dataid ?? '') + '|' + String(code ?? '') + '|' + String(keyinfo ?? '');
    let entry = state.master.get(key);
    if (entry === undefined) {
        entry = { count: 0, notFound: 0, totalMs: 0, maxMs: 0 };
        state.master.set(key, entry);
    }
    entry.count += 1;
    state.requests += 1;
    state.inflight += 1;
    if (state.inflight > state.maxInflight) state.maxInflight = state.inflight;
    if (state.firstRequestAt === 0) state.firstRequestAt = now();
    return { key: key, entry: entry, t0: now() };
}

export function perfMasterResponse(token, found) {
    if (!ENABLED || token === null || token === undefined) return;
    const ms = now() - token.t0;
    token.entry.totalMs += ms;
    if (ms > token.entry.maxMs) token.entry.maxMs = ms;
    if (!found) token.entry.notFound += 1;
    state.inflight -= 1;
    state.lastResponseAt = now();
    scheduleDump();
}

// 応答が3秒途切れたら要約を出す。飛行中が残っていても出す(未応答件数も表示する)。
function scheduleDump() {
    if (dumpTimer !== null) clearTimeout(dumpTimer);
    dumpTimer = setTimeout(() => { dumpTimer = null; perfDump(); perfText(); }, 3000);
}

// ---- レンダー / Redux action の回数 ----
export function perfCountRender(name) {
    if (!ENABLED) return;
    state.render.set(name, (state.render.get(name) || 0) + 1);
}

export function perfCountAction(type) {
    if (!ENABLED) return;
    const key = String(type ?? '');
    state.action.set(key, (state.action.get(key) || 0) + 1);
}

// ---- action の発行元を記録する ----
// Error().stack から React/Redux 内部を除いた関数名を拾い、発行元ごとに数える。
const originStats = new Map();
const ORIGIN_SKIP = /^(Object\.|Array\.|HTMLDivElement\.)?(dispatch|notify|notifyNestedSubs|handleChangeWrapper|batchedUpdates.*|flushSyncCallbackQueue.*|runWithPriority.*|unstable_runWithPriority|scheduleUpdateOnFiber|dispatchAction|checkForNestedUpdates|performSyncWorkOnRoot|performUnitOfWork|workLoopSync|renderRootSync|beginWork|updateFunctionComponent|renderWithHooks|useMemo|updateMemo|mountMemo|commitRoot.*|commitLayoutEffects|commitAttachRef|commitWork|commitMutationEffects|callCallback|invokeGuardedCallback.*|flushPassiveEffects.*|invokePassiveEffectCreate|discreteUpdates.*|dispatchDiscreteEvent|Promise\.then|setTimeout|eval)$/;
export function perfActionOrigin(type, stack) {
    if (!ENABLED) return;
    const names = [];
    String(stack || '').split(String.fromCharCode(10)).forEach((line) => {
        if (names.length >= 5) return;
        const m = line.match(/^\s*at ([^( ]+) /);
        if (m === null) return;
        const name = m[1];
        if (ORIGIN_SKIP.test(name)) return;
        if (names[names.length - 1] !== name) names.push(name);
    });
    const key = type + ' ← ' + (names.length === 0 ? '(名前なし)' : names.join(' < '));
    originStats.set(key, (originStats.get(key) || 0) + 1);
}

// ---- useMemo/useEffect の再計算原因を特定する ----
// 前回の deps と Object.is で比較し、変わったものだけ名前で出す。
const whyState = new Map();
export function perfWhyChanged(name, depNames, depValues) {
    if (!ENABLED) return;
    const prev = whyState.get(name);
    whyState.set(name, depValues.slice());
    if (prev === undefined) {
        console.warn('[perf] ' + name + ' 初回計算');
        return;
    }
    const changed = [];
    for (let i = 0; i < depValues.length; i += 1) {
        if (!Object.is(prev[i], depValues[i])) changed.push(depNames[i] || ('#' + i));
    }
    console.warn('[perf] ' + name + ' 再計算 原因=' + (changed.length === 0 ? '(deps同一)' : changed.join(', ')));
}

// ---- メインスレッド停止の監視 ----
// setInterval の遅れ(ドリフト)でブロックを検出し、その間に何が起きたかを差分で出す。
let watchdogTimer = null;
let lastTick = 0;
let lastSnapshot = null;

function snapshot() {
    return {
        render: new Map(state.render),
        action: new Map(state.action),
        origin: new Map(originStats),
        requests: state.requests,
        inflight: state.inflight
    };
}

function diffTop(prevMap, currMap, limit) {
    const rows = [];
    currMap.forEach((count, key) => {
        const delta = count - (prevMap.get(key) || 0);
        if (delta > 0) rows.push({ 名前: key, 回数: delta });
    });
    return rows.sort((a, b) => b.回数 - a.回数).slice(0, limit);
}

export function perfStartWatchdog(thresholdMs, intervalMs) {
    const threshold = thresholdMs || 300;
    const interval = intervalMs || 200;
    if (!ENABLED || watchdogTimer !== null) return;
    lastTick = now();
    lastSnapshot = snapshot();
    watchdogTimer = setInterval(() => {
        const t = now();
        const drift = t - lastTick - interval;
        lastTick = t;
        const curr = snapshot();
        if (drift >= threshold) {
            const renderDelta = diffTop(lastSnapshot.render, curr.render, 5);
            const actionDelta = diffTop(lastSnapshot.action, curr.action, 8);
            const reqDelta = curr.requests - lastSnapshot.requests;
            state.stalls.push({
                ms: Math.round(drift),
                render: renderDelta,
                action: actionDelta,
                newRequests: reqDelta,
                inflight: curr.inflight
            });
            // 1行のプレーンテキストで出す。console.table はログをファイル保存すると中身が落ちるため。
            const renderText = renderDelta.length === 0 ? 'なし' : renderDelta.map((x) => x.名前 + '=' + x.回数).join(' ');
            const actionText = actionDelta.length === 0 ? 'なし' : actionDelta.map((x) => x.名前 + '=' + x.回数).join(' ');
            const originDelta = diffTop(lastSnapshot.origin, curr.origin, 6);
            const originText = originDelta.length === 0 ? 'なし' : originDelta.map((x) => x.名前 + '=' + x.回数).join(' / ');
            console.warn(
                '[perf] メインスレッド停止 ' + Math.round(drift) + 'ms' +
                ' | 新規マスタ要求=' + reqDelta + ' 未応答=' + curr.inflight +
                ' | render: ' + renderText +
                ' | action: ' + actionText
            );
            console.warn('[perf] 発行元: ' + originText);
        }
        lastSnapshot = curr;
    }, interval);

    // ブラウザが検出する長時間タスクも拾う(対応ブラウザのみ)。
    try {
        if (typeof PerformanceObserver === 'function') {
            const observer = new PerformanceObserver((list) => {
                list.getEntries().forEach((entry) => {
                    if (entry.duration >= threshold) {
                        console.warn('[perf] longtask ' + Math.round(entry.duration) + 'ms');
                    }
                });
            });
            observer.observe({ entryTypes: ['longtask'] });
        }
    } catch (error) { /* 未対応ブラウザは無視 */ }
}

// ---- 出力 ----
export function perfDump() {
    if (!ENABLED) return;
    const rows = [...state.master.entries()]
        .map(([key, e]) => ({
            キー: key,
            発行回数: e.count,
            ゼロ件: e.notFound,
            平均ms: e.count === 0 ? 0 : Math.round(e.totalMs / e.count),
            最大ms: Math.round(e.maxMs)
        }))
        .sort((a, b) => b.発行回数 - a.発行回数);
    const unique = rows.length;
    const total = rows.reduce((sum, r) => sum + r.発行回数, 0);
    const elapsed = state.lastResponseAt === 0 ? 0 : Math.round(state.lastResponseAt - state.firstRequestAt);

    console.log(
        '%c[perf] マスタ照会: 発行 ' + total + '回 / ユニーク ' + unique + '種 ' +
        '(重複率 ' + (unique === 0 ? 0 : (total / unique).toFixed(1)) + '倍) / ' +
        '最大同時飛行 ' + state.maxInflight + ' / 未応答 ' + state.inflight + '件 / 経過 ' + elapsed + 'ms',
        'font-weight:bold'
    );
    if (rows.length !== 0 && typeof console.table === 'function') console.table(rows.slice(0, 20));

    if (state.spans.length !== 0 && typeof console.table === 'function') {
        console.log('[perf] 区間計測');
        console.table(state.spans.map((s) => ({ 区間: s.name, ms: Math.round(s.ms), 備考: s.extra })));
    }
    if (state.render.size !== 0 && typeof console.table === 'function') {
        console.log('[perf] レンダー回数(累計)');
        console.table([...state.render.entries()].map(([k, v]) => ({ コンポーネント: k, 回数: v })));
    }
    if (state.action.size !== 0 && typeof console.table === 'function') {
        console.log('[perf] Redux action 回数(累計・上位20)');
        console.table([...state.action.entries()]
            .map(([k, v]) => ({ action: k, 回数: v }))
            .sort((a, b) => b.回数 - a.回数)
            .slice(0, 20));
    }
    if (state.stalls.length !== 0) {
        const maxStall = state.stalls.reduce((m, s) => (s.ms > m ? s.ms : m), 0);
        console.log('[perf] メインスレッド停止 ' + state.stalls.length + '回 (最大 ' + maxStall + 'ms)');
        if (typeof console.table === 'function') {
            console.table(state.stalls.map((s) => ({ 停止ms: s.ms, 新規要求: s.newRequests, 未応答: s.inflight })));
        }
    }
}

// 表形式ではなくプレーンテキストで出す。ログをファイル保存/コピーするとき用。
export function perfText() {
    const lines = [];
    const rows = [...state.master.entries()]
        .map(([key, e]) => ({ key: key, count: e.count, notFound: e.notFound, avg: e.count === 0 ? 0 : Math.round(e.totalMs / e.count), max: Math.round(e.maxMs) }))
        .sort((a, b) => b.count - a.count);
    const total = rows.reduce((sum, r) => sum + r.count, 0);
    const elapsed = state.lastResponseAt === 0 ? 0 : Math.round(state.lastResponseAt - state.firstRequestAt);
    lines.push('===== perf summary =====');
    lines.push('master: total=' + total + ' unique=' + rows.length + ' maxInflight=' + state.maxInflight + ' pending=' + state.inflight + ' elapsedMs=' + elapsed);
    lines.push('--- master detail (count / notFound / avgMs / maxMs / key) ---');
    rows.slice(0, 40).forEach((r) => lines.push('  ' + r.count + ' / ' + r.notFound + ' / ' + r.avg + ' / ' + r.max + ' / ' + r.key));
    lines.push('--- spans ---');
    state.spans.forEach((s) => lines.push('  ' + Math.round(s.ms) + 'ms  ' + s.name + (s.extra ? ' ' + s.extra : '')));
    lines.push('--- render count ---');
    state.render.forEach((v, k) => lines.push('  ' + v + '  ' + k));
    lines.push('--- redux action count (top 20) ---');
    [...state.action.entries()].sort((a, b) => b[1] - a[1]).slice(0, 20).forEach(([k, v]) => lines.push('  ' + v + '  ' + k));
    lines.push('--- action origins (top 20) ---');
    [...originStats.entries()].sort((a, b) => b[1] - a[1]).slice(0, 20).forEach(([k, v]) => lines.push('  ' + v + '  ' + k));
    lines.push('--- main thread stalls (' + state.stalls.length + ') ---');
    state.stalls.forEach((s) => {
        const r = s.render.map((x) => x.名前 + '=' + x.回数).join(', ');
        const a = s.action.map((x) => x.名前 + '=' + x.回数).join(', ');
        lines.push('  ' + s.ms + 'ms  newReq=' + s.newRequests + ' pending=' + s.inflight + ' render[' + r + '] action[' + a + ']');
    });
    const text = lines.join(String.fromCharCode(10));
    console.log(text);
    return text;
}

export function perfReset() {
    state.spans = [];
    state.master.clear();
    state.requests = 0;
    state.inflight = 0;
    state.maxInflight = 0;
    state.firstRequestAt = 0;
    state.lastResponseAt = 0;
    state.render.clear();
    state.action.clear();
    state.stalls = [];
    originStats.clear();
    lastSnapshot = snapshot();
    console.log('[perf] 集計をクリアしました');
}

if (ENABLED && typeof window !== 'undefined') {
    window.__perf = {
        dump: perfDump,
        text: perfText,
        copy: () => {
            const text = perfText();
            if (typeof navigator !== 'undefined' && navigator.clipboard) navigator.clipboard.writeText(text);
            return text;
        },
        reset: perfReset,
        watch: perfStartWatchdog,
        raw: () => ({
            spans: state.spans.slice(),
            master: [...state.master.entries()],
            render: [...state.render.entries()],
            action: [...state.action.entries()],
            stalls: state.stalls.slice(),
            inflight: state.inflight,
            maxInflight: state.maxInflight
        })
    };
    // 監視は読み込み時に自動で開始する。
    perfStartWatchdog();
}
